import { useEffect, useId, useRef, useState } from 'react';
import styles from './MultiSelectDropdown.module.css';

export interface MultiSelectOption {
  value: string;
  label: string;
}

interface Props {
  label: string;
  options: MultiSelectOption[];
  selected: string[];
  onChange: (selected: string[]) => void;
  placeholder?: string;
}

// A button that opens a checkbox list. The value keeps the options' order, so
// what is saved does not depend on the order things were ticked in.
const MultiSelectDropdown = ({ label, options, selected, onChange, placeholder = 'None selected' }: Props) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const listId = useId();

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && e.target instanceof Node && !rootRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const toggle = (value: string) => {
    const next = selected.includes(value) ? selected.filter((v) => v !== value) : [...selected, value];
    onChange(options.map((o) => o.value).filter((v) => next.includes(v)));
  };

  const names = options.filter((o) => selected.includes(o.value)).map((o) => o.label);
  const summary = names.length === 0 ? placeholder
    : names.length === options.length ? 'All modules'
      : names.length <= 2 ? names.join(', ') : `${names.length} selected`;

  return (
    <div className={styles.root} ref={rootRef}>
      <span className={styles.label}>{label}</span>
      <button
        type="button"
        className={styles.trigger}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listId}
        aria-label={`${label}: ${summary}`}
        onClick={() => setOpen((o) => !o)}
      >
        <span className={names.length ? '' : styles.placeholder}>{summary}</span>
        <span className="material-symbols-outlined text-base" aria-hidden="true">{open ? 'expand_less' : 'expand_more'}</span>
      </button>
      {open && (
        <div className={styles.panel} id={listId} role="listbox" aria-multiselectable="true" aria-label={label}>
          <div className={styles.bulk}>
            <button type="button" onClick={() => onChange(options.map((o) => o.value))}>Select all</button>
            <button type="button" onClick={() => onChange([])}>Clear</button>
          </div>
          {options.map((o) => (
            <label key={o.value} className={styles.option} role="option" aria-selected={selected.includes(o.value)}>
              <input type="checkbox" checked={selected.includes(o.value)} onChange={() => toggle(o.value)} className="accent-primary" />
              <span>{o.label}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
};

export default MultiSelectDropdown;
